import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import BuildingsContext from '@totem/components/orgManagement/orgDetails/buildings/applied/buildingsContext';
import { Building } from '@totem/components/orgManagement/orgDetails/buildings/applied/types';
import { Organization } from '@totem/components/orgManagement/types';
import BuildingMoveDialog from '@totem/components/orgManagement/uiComponents/buildingMoveDialog/buildingMoveDialog';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { ORGANIZATION_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

type Props = {
  organization: Organization;
  children?: ReactNode;
};

const BuildingsContainer = ({ children, organization }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [isSending, setIsSending] = useState(false);
  const [data, setData] = useState<Building[]>([]);
  const [action, setAction] = useState<string>('');
  const [selectedBuilding, setSelectedBuilding] = useState<Building>(null);

  useEffect(() => {
    setRefreshData(true);
  }, [userProfile.organization.id, organization]);

  useEffect(() => {
    if (!refreshData) {
      return;
    }
    setRefreshData(false);

    fetch(`${ORGANIZATION_ENDPOINT}/${organization.id}/buildings`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: Building[]) => {
        setData(result);
      });
  }, [refreshData]);

  const sendBuildingNetSuiteKeyUpdate = useCallback(
    async (
      keyAction: string,
      organizationId: string,
      buildingId: string,
      netSuiteKey: string,
    ) => {
      let method = 'GET';
      if (keyAction === 'DELETE') {
        method = 'DELETE';
      }
      if (keyAction === 'ADD') {
        method = 'PUT';
      }

      if (!isSending) {
        fetch(
          `${ORGANIZATION_ENDPOINT}/${organizationId}/buildings/${buildingId}/addresses/${netSuiteKey}`,
          {
            method,
            headers: new Headers({
              Authorization: `Bearer ${getApiToken()}`,
            }),
          },
        ).then((res) => {
          setIsSending(false);
          const success = CheckResponseShowError(res);
          if (success) {
            setRefreshData(true);
          }
        });
      }
    }, // End async
    [],
  );

  const updateBuildingNetSuiteKey = (
    keyAction: string,
    organizationId: string,
    buildingId: string,
    netSuiteKey: string,
  ) => {
    sendBuildingNetSuiteKeyUpdate(
      keyAction,
      organizationId,
      buildingId,
      netSuiteKey,
    );
  };

  const handleAction = (newAction: string, newBuilding: Building) => {
    setSelectedBuilding(newBuilding);

    setAction(newAction);
  };

  return (
    <BuildingsContext.Provider
      value={{
        buildings: data,
        onAction: handleAction,
        action,
        selectedBuilding,
        onRefresh: () => setRefreshData(true),
        onUpdateBuildingNetSuiteKey: updateBuildingNetSuiteKey,
      }}
    >
      {children}
      {action === 'building-move' && isNotNull(selectedBuilding) && (
        <BuildingMoveDialog
          currentBuilding={selectedBuilding}
          open={action === 'building-move'}
          onClose={() => {
            setAction('');

            setSelectedBuilding(null);
          }}
        />
      )}
    </BuildingsContext.Provider>
  );
};

export default BuildingsContainer;
